import * as THREE from "three";

export interface SceneCtx {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  sun: THREE.DirectionalLight;
  hemi: THREE.HemisphereLight;
  /** Playback control; zero-dt frames let orbit controls keep responding. */
  paused?: boolean;
  /** Per-frame hooks, called with dt (seconds) and elapsed time. */
  onFrame: Set<(dt: number, t: number) => void>;
}

export function createScene(container: HTMLElement): SceneCtx {
  const renderer = new THREE.WebGLRenderer({ antialias: true });
  // Glass marbles (transmission) sample a scene texture — half-res is
  // visually free and halves the extra pass (r172+).
  renderer.transmissionResolutionScale = 0.5;
  renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.NeutralToneMapping;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  container.appendChild(renderer.domElement);

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x87b7e0);
  scene.fog = new THREE.Fog(0x87b7e0, 40, 140);

  const camera = new THREE.PerspectiveCamera(55, 1, 0.1, 500);
  camera.position.set(12, 10, 12);

  const hemi = new THREE.HemisphereLight(0xbfd8ff, 0x8a6f5a, 1.1);
  scene.add(hemi);

  const sun = new THREE.DirectionalLight(0xfff4e0, 2.2);
  sun.position.set(18, 30, 12);
  sun.castShadow = true;
  sun.shadow.mapSize.set(2048, 2048);
  sun.shadow.bias = -0.0004;
  sun.shadow.normalBias = 0.02;
  scene.add(sun);
  scene.add(sun.target);

  const onFrame = new Set<(dt: number, t: number) => void>();

  function resize() {
    const w = container.clientWidth || innerWidth;
    const h = container.clientHeight || innerHeight;
    renderer.setSize(w, h);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  }
  addEventListener("resize", resize);
  resize();

  const ctx: SceneCtx = {
    renderer,
    scene,
    camera,
    sun,
    hemi,
    onFrame,
    paused: false,
  };
  const clock = new THREE.Clock();
  renderer.setAnimationLoop(() => {
    const dt = Math.min(clock.getDelta(), 0.1);
    const t = clock.elapsedTime;
    for (const fn of onFrame) fn(ctx.paused ? 0 : dt, t);
    renderer.render(scene, camera);
  });

  return ctx;
}

/** Fit the sun's shadow frustum tightly around a world-space bounding box. */
export function fitShadowsTo(
  sun: THREE.DirectionalLight,
  box: THREE.Box3,
): void {
  const center = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());
  const radius = Math.max(size.x, size.z) * 0.75 + 4;
  sun.target.position.copy(center);
  sun.position.copy(center).add(
    new THREE.Vector3(0.6, 1.2, 0.4).multiplyScalar(radius * 1.6),
  );
  const cam = sun.shadow.camera;
  cam.left = -radius;
  cam.right = radius;
  cam.top = radius;
  cam.bottom = -radius;
  cam.near = 0.5;
  cam.far = radius * 5;
  cam.updateProjectionMatrix();
}
